import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { getAppContext } from '../utils/getAppContext';
import { getBrandConfig } from '../utils/brandConfig';

type ProtectedRouteProps = {
    children: React.ReactNode;
    requiredPlatformRoles?: string[];
    requiredSystemRoles?:   string[];
    requiredOrgRoles?:      string[];
    requiredPermissions?:   string[];
};

const PLATFORM_ROLE_HIERARCHY: string[] = ['User', 'Admin', 'Billing-Admin'];
const VALID_SYSTEM_ROLES = new Set(['Super-Admin']);

export default function ProtectedRoute({
    children,
    requiredPlatformRoles,
    requiredSystemRoles,
    requiredOrgRoles,
    requiredPermissions,
}: ProtectedRouteProps) {
    const { isAuthenticated, roles } = useAuth();

    if (isAuthenticated === null) return null;
    if (!isAuthenticated) {
        console.log('[ProtectedRoute] BLOCKED — not authenticated');
        // Bounce to the current brand's own login path, not "/" — via the
        // root, the "Connecting…" screen would flash AskSmart branding.
        const fallback = getBrandConfig(getAppContext()).loginPath;
        return <Navigate to={fallback} replace />;
    }

    const { platformRoles, systemRoles, orgRoles, permissions } = roles;

    // Wait for permissions to be hydrated before evaluating permission checks.
    // On refresh, platformRoles may load from JWT while permissions (from DB) are still empty.
    if (requiredPermissions && requiredPermissions.length > 0 && permissions.length === 0) {
        return null;
    }

    console.log('[ProtectedRoute] User state:', {
        platformRoles,
        systemRoles,
        orgRoles,
        permissions,
        requiredPlatformRoles,
        requiredSystemRoles,
        requiredOrgRoles,
        requiredPermissions,
    });

    // ── System role check ─────────────────────────────────────────────────────
    if (requiredSystemRoles && requiredSystemRoles.length > 0) {
        const hasRole = requiredSystemRoles.some((r) => {
            if (!VALID_SYSTEM_ROLES.has(r)) return false;
            return systemRoles.includes(r);
        });

        console.log(`[ProtectedRoute] System role check — required: [${requiredSystemRoles}] | user has: [${systemRoles}] | passed: ${hasRole}`);
        if (!hasRole) {
            console.log('[ProtectedRoute] BLOCKED — insufficient system role');
            return <Navigate to="/unauthorized" replace />;
        }
    }

    // ── Platform role check ───────────────────────────────────────────────────
    if (requiredPlatformRoles && requiredPlatformRoles.length > 0) {
        const userMaxIndex = Math.max(-1, ...platformRoles.map((r) => PLATFORM_ROLE_HIERARCHY.indexOf(r)));

        const hasRole = requiredPlatformRoles.some((required) => {
            const requiredIndex = PLATFORM_ROLE_HIERARCHY.indexOf(required);
            if (requiredIndex === -1) return false;
            return userMaxIndex >= requiredIndex;
        });

        console.log(`[ProtectedRoute] Platform role check — required: [${requiredPlatformRoles}] | user has: [${platformRoles}] (maxIndex: ${userMaxIndex}) | passed: ${hasRole}`);
        if (!hasRole) {
            console.log('[ProtectedRoute] BLOCKED — insufficient platform role');
            return <Navigate to="/unauthorized" replace />;
        }
    }

    // ── Org role check ────────────────────────────────────────────────────────
    if (requiredOrgRoles && requiredOrgRoles.length > 0) {
        const hasOrgRole = requiredOrgRoles.some((r) => orgRoles.includes(r));
        console.log(`[ProtectedRoute] Org role check — required: [${requiredOrgRoles}] | user has: [${orgRoles}] | passed: ${hasOrgRole}`);
        if (!hasOrgRole) {
            console.log('[ProtectedRoute] BLOCKED — insufficient org role');
            return <Navigate to="/unauthorized" replace />;
        }
    }

    // ── Permissions check ─────────────────────────────────────────────────────
    if (requiredPermissions && requiredPermissions.length > 0) {
        const hasAllPermissions = requiredPermissions.every((p) => permissions.includes(p));
        console.log(`[ProtectedRoute] Permission check — required: [${requiredPermissions}] | user has: [${permissions}] | passed: ${hasAllPermissions}`);
        if (!hasAllPermissions) {
            console.log('[ProtectedRoute] BLOCKED — insufficient permissions');
            return <Navigate to="/unauthorized" replace />;
        }
    }

    console.log('[ProtectedRoute] ALLOWED — all checks passed');
    return <>{children}</>;
}